import React, { useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RequireRole, RequireFeature } from './routes/guards';
import { ROUTES } from './lib/constants/routes';

// Layouts
import { AuthLayout } from './routes/layouts/AuthLayout';
import { AppLayout } from './routes/layouts/AppLayout';
import AdminShellLayout from '@/components/shared/layout/AdminShellLayout';

// Routes
import StudentRoutes from './routes/StudentRoutes';
import MasterRoutes from './routes/MasterRoutes';
import { adminRoutes } from './routes/config/adminRoutes';

// Common Pages
import LandingPage from './pages/main/LandingPage';
import { ClassCurriculum, OpenClassList, ClassDetail, SessionManagement, EvaluationResults } from './pages/class';
import StudentEvaluationResults from './pages/class/StudentEvaluationResults';
import StudentPracticeStatus from './pages/class/StudentPracticeStatus';
import PracticeStatus from './pages/class/PracticeStatus';
import PracticeStatistics from './pages/class/PracticeStatistics';
import PracticeRanking from './pages/class/PracticeRanking';
import { SimulationResultsRoute } from './pages/class/SimulationResults';
import ClassInviteAccept from './pages/class/ClassInviteAccept';
import OrderConfirm from './pages/order/OrderConfirm';
import Cart from './pages/order/Cart';
import PaymentTossSuccess from './pages/order/PaymentTossSuccess';
import PaymentTossFail from './pages/order/PaymentTossFail';
import ClassSession from './pages/class/ClassSession';
import { ScenarioDetail, ScenarioList } from './pages/scenario';
import ResourceList from './pages/resources/ResourceList';
import ResourceDetail from './pages/resources/ResourceDetail';
import BlogList from './pages/blog/BlogList';
import BlogDetail from './pages/blog/BlogDetail';
import ContactUs from './pages/contact/ContactUs';
import TermsOfService from './pages/legal/TermsOfService';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import OrgPage from './pages/manager/OrgPage';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import SignupComplete from './pages/auth/SignupComplete';
import EmailVerification from './pages/auth/EmailVerification';
import EmailVerificationComplete from './pages/auth/EmailVerificationComplete';
import StudioEdit from './pages/studio/StudioEdit';
import EmailPreview from './pages/dev/EmailPreview';
import PasswordResetRequest from './pages/auth/PasswordResetRequest';
import PasswordResetNew from './pages/auth/PasswordResetNew';
import EmailChange from './pages/auth/EmailChange';
import EmailChangeComplete from './pages/auth/EmailChangeComplete';
import AccountDeletionRequest from './pages/auth/AccountDeletionRequest';
import AccountDeletionConfirm from './pages/auth/AccountDeletionConfirm';
import AccountDeletionComplete from './pages/auth/AccountDeletionComplete';
import Settings from './pages/auth/Settings';
import ChangeMyPassword from './pages/auth/ChangeMyPassword';
import AddAccount from './pages/auth/AddAccount';
import KhaVerify from './pages/auth/KhaVerify';
import SsoLogin from './pages/auth/sso/SsoLogin';
import SsoSignup from './pages/auth/sso/SsoSignup';
import SsoLink from './pages/auth/sso/SsoLink';
import SsoLinkConnect from './pages/auth/sso/SsoLinkConnect';
import SsoSignupForm from './pages/auth/sso/SsoSignupForm';

// Error Pages
import { NotFoundPage, ForbiddenPage } from './routes/pages/ErrorPages';

import { Toaster } from '@/components/shared/ui/toaster';
import I18nQueryInvalidation from '@/components/shared/I18nQueryInvalidation';
import { TranslationLoadingProvider } from '@/contexts/TranslationLoadingContext';

import './App.css';

function toNumericOrgId(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const stripped = String(raw).replace(/^ORG/i, '').trim();
  const num = parseInt(stripped, 10);
  return isNaN(num) ? (typeof raw === 'string' ? raw : undefined) : String(num);
}

function MasterOrganizationRoute(): React.ReactElement {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const overrideId = toNumericOrgId(user?.currentAccount?.organizationId ?? null);
  if (!overrideId) {
    return (
      <div className="max-w-[1460px] mx-auto px-5 py-6">
        <p className="text-gray-600">{t('orgLoadError')}</p>
      </div>
    );
  }
  return <OrgPage overrideId={overrideId} />;
}

function MasterOrganizationsIdRedirect(): React.ReactElement {
  const { search } = useLocation();
  return <Navigate to={`${ROUTES.MASTER.ORGANIZATION}${search}`} replace />;
}

/**
 * 문서 `lang`/`dir` 동기화 (캘린더·접근성·RTL 레이아웃).
 */
function DocumentLangSync(): null {
  const { i18n } = useTranslation();
  useEffect(() => {
    const raw = i18n.language || 'en';
    const base = raw.toLowerCase().split('-')[0];
    let docLang = raw;
    if (base === 'en') docLang = 'en-US';
    else if (base === 'ko') docLang = 'ko';
    else if (base === 'ar') docLang = 'ar';
    else if (base === 'mn') docLang = 'mn';
    else if (base === 'id') docLang = 'id';
    document.documentElement.lang = docLang;
    document.documentElement.dir = base === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);
  return null;
}

function ScrollToTop(): null {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) return;
    document.querySelectorAll<HTMLElement>('[data-page-scroll-container]').forEach((container) => {
      container.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return null;
}

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TranslationLoadingProvider>
          <DocumentLangSync />
          <I18nQueryInvalidation />
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
          }}
        >
          <ScrollToTop />
          <Routes>
          {/* =======================================================
              AuthLayout: 인증 페이지들 (GNB/Footer 없음)
          ======================================================== */}
          <Route element={<AuthLayout />}>
            {/* Landing & Marketing */}
            <Route path={ROUTES.HOME} element={<LandingPage />} />

            {/* Authentication Pages */}
            <Route path={ROUTES.AUTH.LOGIN} element={<Login />} />
            <Route path={ROUTES.AUTH.SIGNUP} element={<Signup />} />
            <Route path={ROUTES.AUTH.SIGNUP_COMPLETE} element={<SignupComplete />} />
            <Route path={ROUTES.AUTH.EMAIL_VERIFICATION} element={<EmailVerification />} />
            <Route path={ROUTES.AUTH.EMAIL_VERIFICATION_COMPLETE} element={<EmailVerificationComplete />} />
            <Route path={ROUTES.AUTH.PASSWORD_RESET} element={<PasswordResetRequest />} />
            <Route path={ROUTES.AUTH.PASSWORD_RESET_NEW} element={<PasswordResetNew />} />
            
            {/* 
              🚨 임시 라우트 (TODO: 제거 필요!)
              
              추가 날짜: 2024-01-21
              추가 이유: 백엔드가 이메일에 보내는 URL이 /change-password 로 되어있음
              정상 경로: /password-reset/new
              
              제거 조건: 백엔드가 이메일 템플릿을 /password-reset/new 로 수정한 후
              관련 문서: .rules/task/BACKEND_PROFILE_REQUESTS.md (4️⃣번 항목)
              
              ⚠️ 백엔드 수정 완료 후 반드시 제거할 것!
            */}
            <Route path="/change-password" element={<PasswordResetNew />} />
            
            <Route path={ROUTES.AUTH.ACCOUNT_DELETION_REQUEST} element={<AccountDeletionRequest />} />
            <Route path={ROUTES.AUTH.ACCOUNT_DELETION_CONFIRM} element={<AccountDeletionConfirm />} />
            <Route path={ROUTES.AUTH.ACCOUNT_DELETION_COMPLETE} element={<AccountDeletionComplete />} />
            <Route path={ROUTES.AUTH.EMAIL_CHANGE} element={
              <RequireRole role="student">
                <EmailChange />
              </RequireRole>
            } />
            <Route path={ROUTES.AUTH.EMAIL_CHANGE_COMPLETE} element={<EmailChangeComplete />} />
            <Route path={ROUTES.AUTH.SETTINGS} element={<Settings />} />
            <Route path="/settings/change-password" element={<ChangeMyPassword />} />
            <Route path={ROUTES.AUTH.ADD_ACCOUNT} element={<AddAccount />} />
            <Route path={ROUTES.AUTH.KHA_VERIFY} element={<KhaVerify />} />

            {/* SSO Routes */}
            <Route path="/sso/:origin/login/:userSsoAccessId" element={<SsoLogin />} />
            <Route path="/sso/:origin/signup/:userSsoAccessId" element={<SsoSignup />} />
            <Route path="/sso/:origin/link/:userSsoAccessId" element={<SsoLink />} />
            <Route path="/sso/:origin/link/:userSsoAccessId/form" element={<SsoLinkConnect />} />
            <Route path="/sso/:origin/signup/:userSsoAccessId/form" element={<SsoSignupForm />} />

            {/* Development Tools - Feature Flag Protected */}
            <Route element={<RequireFeature feature="ENABLE_EMAIL_PREVIEW" />}>
              <Route path={ROUTES.DEV.EMAIL_PREVIEW} element={<EmailPreview />} />
            </Route>

          </Route>

          {/* =======================================================
              Full Screen Pages (No Layout)
              - 독자적인 레이아웃이 필요한 페이지들
          ======================================================== */}
          
          {/* Studio: Full-screen Editor - Feature Flag + Role Protected */}
          <Route element={<RequireFeature feature="FEATURE_STUDIO_EDITOR" />}>
            <Route element={<RequireRole role="master" />}>
              <Route path={ROUTES.STUDIO.EDIT} element={<StudioEdit />} />
            </Route>
          </Route>

          {/* Session Management: Full Screen (Custom Header with Tabs) */}
          <Route path="/admin/class-management/:classId/session-management" element={
            <RequireRole role="admin">
              <SessionManagement />
            </RequireRole>
          } />
          <Route path="/master/class-management/:classId/session-management" element={
            <RequireRole role="master">
              <SessionManagement />
            </RequireRole>
          } />

          {/* Evaluation Results: Full Screen (Custom Header with Tabs) */}
          <Route path="/admin/class-management/:classId/evaluation" element={
            <RequireRole role="admin">
              <EvaluationResults />
            </RequireRole>
          } />
          <Route path="/master/class-management/:classId/evaluation" element={
            <RequireRole role="master">
              <EvaluationResults />
            </RequireRole>
          } />
          <Route path="/student/my-classes/:classId/evaluation" element={
            <RequireRole role="guest">
              <StudentEvaluationResults />
            </RequireRole>
          } />
          <Route path="/student/my-classes/:classId/practice-status" element={
            <RequireRole role="guest">
              <StudentPracticeStatus />
            </RequireRole>
          } />

          {/* Practice Status: Full Screen (Custom Header with Tabs) */}
          <Route path="/admin/class-management/:classId/practice-status" element={
            <RequireRole role="admin">
              <PracticeStatus />
            </RequireRole>
          } />
          <Route path="/master/class-management/:classId/practice-status" element={
            <RequireRole role="master">
              <PracticeStatus />
            </RequireRole>
          } />

          {/* Practice Statistics: Full Screen (Custom Header with Tabs) */}
          <Route path="/admin/class-management/:classId/statistics" element={
            <RequireRole role="admin">
              <PracticeStatistics />
            </RequireRole>
          } />
          <Route path="/master/class-management/:classId/statistics" element={
            <RequireRole role="master">
              <PracticeStatistics />
            </RequireRole>
          } />

          {/* Practice Ranking: Full Screen (Custom Header with Tabs) */}
          <Route path="/admin/class-management/:classId/ranking" element={
            <RequireRole role="admin">
              <PracticeRanking />
            </RequireRole>
          } />
          <Route path="/master/class-management/:classId/ranking" element={
            <RequireRole role="master">
              <PracticeRanking />
            </RequireRole>
          } />

          {/* Class Curriculum: Full Screen (Custom Header) */}
          <Route path="/admin/class-management/:classId/curriculum" element={
            <RequireRole role="admin">
              <ClassCurriculum />
            </RequireRole>
          } />
          <Route path="/master/class-management/:classId/curriculum" element={
            <RequireRole role="master">
              <ClassCurriculum />
            </RequireRole>
          } />
          <Route path="/class/:classId/curriculum" element={
            <RequireRole role="guest">
              <ClassCurriculum />
            </RequireRole>
          } />

          {/* Class Session: Full Screen (Custom Header) */}
          <Route path="/admin/class-management/:classId/curriculum/:sessionId" element={
            <RequireRole role="admin">
              <ClassSession />
            </RequireRole>
          } />
          <Route path="/master/class-management/:classId/curriculum/:sessionId" element={
            <RequireRole role="master">
              <ClassSession />
            </RequireRole>
          } />
          <Route path="/student/my-classes/:classId/curriculum/:sessionId" element={
            <RequireRole role="guest">
              <ClassSession />
            </RequireRole>
          } />
          <Route path="/class/:classId/curriculum/:sessionId" element={
            <RequireRole role="guest">
              <ClassSession />
            </RequireRole>
          } />

          {/* Class Session Results: Full Screen (사용자별 평가결과 — userId 있으면 해당 사용자만 표시) */}
          <Route path="/admin/class-management/:classId/results/:sessionId/user/:userId" element={
            <RequireRole role="admin">
              <SimulationResultsRoute />
            </RequireRole>
          } />
          <Route path="/admin/class-management/:classId/results/:sessionId" element={
            <RequireRole role="admin">
              <SimulationResultsRoute />
            </RequireRole>
          } />
          <Route path="/master/class-management/:classId/results/:sessionId/user/:userId" element={
            <RequireRole role="master">
              <SimulationResultsRoute />
            </RequireRole>
          } />
          <Route path="/master/class-management/:classId/results/:sessionId" element={
            <RequireRole role="master">
              <SimulationResultsRoute />
            </RequireRole>
          } />
          <Route path="/student/my-classes/:classId/results/:sessionId/user/:userId" element={
            <RequireRole role="guest">
              <SimulationResultsRoute />
            </RequireRole>
          } />
          <Route path="/class/:classId/results/:sessionId/user/:userId" element={
            <RequireRole role="guest">
              <SimulationResultsRoute />
            </RequireRole>
          } />
          <Route path="/class/:classId/results/:sessionId" element={
            <RequireRole role="guest">
              <SimulationResultsRoute />
            </RequireRole>
          } />

          {/* Organization Detail: Full Screen (no GNB/Footer) - Admin 기관 상세 */}
          <Route path="/admin/organizations/:id" element={
            <RequireRole role="admin">
              <OrgPage />
            </RequireRole>
          } />

          {/* Master 관리자 설정: Full Screen (no GNB/Footer) - OrgPage에 기관 ID 전달 */}
          <Route path="/master/organization" element={
            <RequireRole role="master">
              <MasterOrganizationRoute />
            </RequireRole>
          } />
          {/* 마스터는 기관 상세 경로가 없음. 잘못된 /master/organizations/:id 접근은 관리자 설정으로 보냄 */}
          <Route path="/master/organizations/:id" element={
            <RequireRole role="master">
              <MasterOrganizationsIdRedirect />
            </RequireRole>
          } />

          {/* Admin 관리자: /admin/organization 진입 시 기관 목록으로 리다이렉트 */}
          <Route path={ROUTES.ADMIN.ORGANIZATION} element={
            <RequireRole role="admin">
              <Navigate to={ROUTES.ADMIN.ORGANIZATIONS} replace />
            </RequireRole>
          } />

          {/* =======================================================
              AppLayout: 일반 앱 페이지들 (default width)
              - Student, Master 페이지
          ======================================================== */}
          <Route element={<AppLayout />}>
            {/* Student Section */}
            <Route element={<RequireRole role="guest" />}>
              <Route path={`${ROUTES.STUDENT.BASE}/*`} element={<StudentRoutes />} />
            </Route>

            {/* Master Section */}
            <Route element={<RequireRole role="master" />}>
              <Route path={`${ROUTES.MASTER.BASE}/*`} element={<MasterRoutes />} />
            </Route>
            {/* Common / Legacy Routes */}
            {/* 오픈클래스: 로그인 없이 접근 가능 */}
            <Route path={ROUTES.COMMON.CLASS_LIST} element={<OpenClassList />} />
            {/* 블로그: GNB 블로그 메뉴 */}
            <Route path={ROUTES.COMMON.BLOG} element={<BlogList />} />
            <Route path="/blog/:id" element={<BlogDetail />} />
            {/* 이용약관: 비로그인 접근 가능 */}
            <Route path={ROUTES.COMMON.TERMS} element={<TermsOfService />} />
            {/* 개인정보처리방침: 비로그인 접근 가능 */}
            <Route path={ROUTES.COMMON.PRIVACY} element={<PrivacyPolicy />} />
            {/* 고객문의: GNB 고객문의 메뉴 */}
            <Route path={ROUTES.COMMON.CONTACT} element={<ContactUs />} />
            {/* 장바구니: 로그인 전에도 이용 가능 */}
            <Route path={ROUTES.COMMON.CART} element={<Cart />} />
            {/* 리소스 목록/상세: GNB 리소스 메뉴 */}
            <Route path={ROUTES.COMMON.RESOURCES} element={<ResourceList />} />
            <Route path="/resources/:id" element={<ResourceDetail />} />
            <Route path="/open-class/:id" element={<ClassDetail />} />
            {/* 클래스 초대 링크: 비로그인 접근 가능 (ClassInviteAccept 내부에서 로그인 유도) */}
            <Route path="/class/invite/:token" element={<ClassInviteAccept />} />
            <Route path={ROUTES.COMMON.ORDER_CONFIRM} element={
              <RequireRole role="guest">
                <OrderConfirm />
              </RequireRole>
            } />
            <Route path={ROUTES.COMMON.PAYMENT_TOSS_SUCCESS} element={
              <RequireRole role="guest">
                <PaymentTossSuccess />
              </RequireRole>
            } />
            <Route path={ROUTES.COMMON.PAYMENT_TOSS_FAIL} element={
              <RequireRole role="guest">
                <PaymentTossFail />
              </RequireRole>
            } />
            <Route path="/scenario/:id" element={<ScenarioDetail />} />
            <Route path={ROUTES.COMMON.SCENARIOS} element={<ScenarioList />} />
          </Route>

          {/* =======================================================
              Admin: 관리자 레이아웃 일괄 적용 (AdminManagerShell)
              - 중첩 라우트로 상세 페이지 useParams 등 정상 동작
          ======================================================== */}
          <Route path={ROUTES.ADMIN.BASE} element={
            <RequireRole role="admin">
              <AdminShellLayout />
            </RequireRole>
          }>
            <Route index element={<Navigate to={ROUTES.ADMIN.ORGANIZATIONS} replace />} />
            {adminRoutes.map(({ path, component: Component }) => (
              <Route
                key={path}
                path={path}
                element={
                  <React.Suspense fallback={
                    <div className="flex justify-center items-center min-h-[320px]">
                      <div className="text-gray-500">Loading...</div>
                    </div>
                  }>
                    <Component />
                  </React.Suspense>
                }
              />
            ))}
          </Route>

          {/* =======================================================
              Error Pages
          ======================================================== */}
          <Route path={ROUTES.ERROR.NOT_FOUND} element={<NotFoundPage />} />
          <Route path={ROUTES.ERROR.FORBIDDEN} element={<ForbiddenPage />} />

          {/* Catch-all: 404 */}
          <Route path="*" element={<Navigate to={ROUTES.ERROR.NOT_FOUND} replace />} />

        </Routes>
        <Toaster />
      </Router>
      </TranslationLoadingProvider>
    </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
